import React from 'react';

import { useSelector } from 'react-redux';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import {
  selectFilesItems,
  selectGroupIdBySlug,
  selectIsAppAvailable,
} from 'store/selectors';
import { GroupAppKey } from 'store/groups/types';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';
import { styles as styleParams } from 'settings/group';

import { Stack } from 'wui/Stack';
import { Button } from 'wui/Button';
import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';
import { Spinner } from 'wui/Spinner';

import { Column, Layout } from '../Layout';
import { RestrictedState } from '../RestrictedState';

import { FileItem } from './FileItem';
import { WIDGET_FILES_EMPTY_STATE } from './dataHooks';

export function FilesPage() {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const router = useRouter();
  const styles = useStyles();
  const { params } = useCurrentStateAndParams();
  const { group$, files$ } = useController();

  const { items, loading, error } = useSelector(selectFilesItems);
  const groupId = useSelector(selectGroupIdBySlug(params.slug));
  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.FILES_APP,
    }),
  );

  useDidUpdate(() => {
    if (isAvailable) {
      files$.fetch(groupId);
    }
  }, [isAvailable]);

  if (!isAvailable) {
    return <RestrictedState application={GroupAppKey.FILES_APP} />;
  }

  if (loading) {
    return <EmptyState wired title={<Spinner />} />;
  }

  if (error) {
    return (
      <ErrorState
        wired
        title={t('groups-web.error-state.title')}
        actionLabel={t('groups-web.error-state.retry.label')}
        onRetry={handleRetry}
      />
    );
  }

  if (!items.length) {
    return (
      <EmptyState
        wired
        data-hook={WIDGET_FILES_EMPTY_STATE}
        title={t('groups-web.files.empty.title')}
        subtitle={t('groups-web.files.empty.description')}
        action={
          <Button onClick={onCreatePost}>
            {t('groups-web.discussion.first.button')}
          </Button>
        }
      />
    );
  }

  return (
    <Layout>
      <Column padding={isMobile ? 'SP0 SP5' : 'SP0'}>
        <Stack direction="vertical" gap={styles.get(styleParams.vSpacing)}>
          {items.map((item) => (
            <FileItem key={item.src?.id} item={item} />
          ))}
        </Stack>
      </Column>
    </Layout>
  );

  function handleRetry() {
    files$.fetch(groupId);
  }

  function onCreatePost() {
    router.go('group.discussion.feed');
    group$.openCreatePostDialog(groupId);
  }
}

FilesPage.displayName = 'FilesPage';
